import {createComponentApp} from "./vueFactory";

/**
 * Creates and mounts all components from the `window.vueComponents` array, if exists.
 *
 * The array must contain objects with the following properties:
 *
 * * `id`: ID of the HTML element to which the component should be mounted.
 * * `name`: Name of the component from the `components` object to create and mount.
 * * `props`: Property values to pass to `propsData` when creating the component.
 *
 * The `window.vueComponents.push()` method is modified so that it initializes a new component each time a new element
 * is pushed to the array.
 *
 * @param {Object} componentsMap Object with all components. Key is name of the component, value is component class.
 */
export function initializeComponents(componentsMap) {
    const componentSpecs = window['vueComponents'] || [];
    for (const componentSpec of componentSpecs) {
        createComponent(componentSpec, componentsMap);
    }
    componentSpecs.push = createPushMethod(componentsMap);
    window['vueComponents'] = componentSpecs;
}


/**
 * @typedef ComponentInstanceSpec
 * @type {object}
 * @property {String} id - ID of the HTML element to which the component should be mounted.
 * @property {String} name - Name of the component from the `components` object to create and mount.
 * @property {Object} props - Property values to pass to `propsData` when creating the component.
 */

/**
 * @callback componentFactoryPushMethod
 * @param {... ComponentInstanceSpec} Component specs to create.
 * @param {Number} New length of the array.
 */


/**
 * Creates and mounts a component to
 *
 * @param {ComponentInstanceSpec} componentSpec Specification of the new component.
 * @param {Object} componentsMap Object with all components. Key is name of the component, value is component class.
 * @return A Vue.js app instance returned by `createApp()`.
 */
function createComponent(componentSpec, componentsMap) {
    const componentInstance = createComponentApp(componentsMap[componentSpec.name], componentSpec.props);
    const mountElement = document.getElementById(componentSpec.id);
    componentInstance.mount(mountElement);
    return componentInstance;
}

/**
 *
 * @param {Object} componentsMap Object with all components. Key is name of the component, value is component class.
 * @return {componentFactoryPushMethod}
 */
function createPushMethod(componentsMap) {
    function pushAndCreateComponent() {
        for (const componentSpec of arguments) {
            createComponent(componentSpec, componentsMap);
        }
        return Array.prototype.push.apply(this, arguments);
    }

    return pushAndCreateComponent;
}
